import { useEffect, useRef } from 'react';
import { Icon } from '@/components/Icon';
import './HowRecordsAreBuilt.css';

const STORAGE_KEY = 'aware.records.explainerSeen';

/** True the first time this user opens Legislative History, false forever after. */
export function shouldAutoOpenExplainer(): boolean {
  return window.localStorage.getItem(STORAGE_KEY) !== '1';
}

export function markExplainerSeen(): void {
  window.localStorage.setItem(STORAGE_KEY, '1');
}

/**
 * "How records are built" — shown automatically on a first visit, then on demand.
 *
 * This exists because the origin of a record was found to be unclear without it. A user
 * looking at Legislative History reasonably assumes they are seeing everything the corpus
 * holds; they are not, and the difference matters. Three steps and a worked example.
 */
export function HowRecordsAreBuilt({ onClose }: { onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    markExplainerSeen();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="how" role="dialog" aria-modal="true" aria-label="How records are built">
      <header className="how__head">
        <div>
          <p className="u-meta">Legislative History</p>
          <h2 className="how__title">How records are built</h2>
        </div>
        <button ref={closeRef} type="button" className="how__close" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="how__body">
        <ol className="how__steps">
          <li>
            <span className="how__stepNum">1</span>
            <div>
              <h3>Your Signals find items</h3>
              <p>
                We don’t list every ordinance in every town — that would be unreadable across
                thousands of jurisdictions. A record starts because something you already told
                us you care about turned up in a meeting.
              </p>
            </div>
          </li>
          <li>
            <span className="how__stepNum">2</span>
            <div>
              <h3>Items sharing a number are grouped</h3>
              <p>
                When two or more meetings in the same town discuss the same spoken ordinance or
                resolution number, they become one record. The number is kept exactly as it was
                said in the meeting.
              </p>
            </div>
          </li>
          <li>
            <span className="how__stepNum">3</span>
            <div>
              <h3>Earlier discussion is linked where we can</h3>
              <p>
                The most useful signal often comes before a number exists — a public comment,
                an early application. Those are linked on a shared name or parcel and marked
                <strong> “No number yet”</strong>, so you can see it’s an inference rather than
                a certainty.
              </p>
            </div>
          </li>
        </ol>

        <section className="how__example">
          <p className="u-meta">A worked example</p>
          <p className="how__exampleLead">
            Your signal <strong>“Solar &amp; storage · NJ”</strong> matched an item in
            Montclair.
          </p>
          <ul className="how__exampleList">
            <li>
              <span className="how__dot" data-hard="false" />
              <span>
                <strong>02/11/2026</strong> — residents raise drainage concerns. No number
                spoken yet, linked on <em>“Riverside Solar LLC”</em>.
              </span>
            </li>
            <li>
              <span className="how__dot" data-hard="true" />
              <span>
                <strong>04/08/2026</strong> — application accepted, and
                <strong> Ordinance 2026-14</strong> is spoken for the first time.
              </span>
            </li>
            <li>
              <span className="how__dot" data-hard="true" />
              <span>
                <strong>05/19/2026</strong> and <strong>06/30/2026</strong> — planning board
                and first reading, both carrying the same number.
              </span>
            </li>
          </ul>
          <p className="how__exampleFoot">
            Three items joined on the number itself. One joined on a shared name — shown in
            amber wherever it appears, because it is a judgment call, not a fact.
          </p>
        </section>

        <p className="how__note">
          <Icon name="check" /> Vote outcomes come from the meeting record itself. Where no
          vote was taken, we say so rather than guessing.
        </p>
      </div>
    </div>
  );
}
